/**
 * Reel Detail Page — single reel shown like the Reels page player, with ALL
 * actions: Like, Comment, Reshare, Share, Save, Delete. Reached by clicking a
 * reel tile on the profile Reels tab. Self-contained (mirrors ReelsPage's
 * player + action rail + comments drawer for ONE reel — no swipe/section
 * toggle). Additive — ReelsPage is untouched.
 */
import { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import reelService, { Reel, ReelComment } from '@/services/reelService'
import { GovLayout, GovButton } from '@/components/layout/GovLayout'
import {
  Heart, MessageCircle, Bookmark, Volume2, VolumeX,
  ChevronDown, X, Send, Trash2, Repeat, ArrowLeft, Flag
} from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { HashtagText } from '@/lib/hashtagLinkify'
import ReportModal from '@/components/common/ReportModal'
import ShareMenu from '@/components/common/ShareMenu'

// ---- local helpers (mirror ReelsPage's module-local versions) ----
const formatCount = (count: number): string => {
  if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`
  if (count >= 1000) return `${(count / 1000).toFixed(1)}K`
  return String(count)
}
const getMediaUrl = (url: string): string => {
  if (!url) return ''
  if (url.startsWith('http')) return url
  if (url.startsWith('/uploads/')) return url
  return url.startsWith('/') ? `/api${url}` : `/api/${url}`
}

const ActionButton = ({
  icon: Icon, count, active, onClick, disabled, activeColor = 'text-red-500', label,
}: {
  icon: React.ComponentType<{ className?: string }>
  count?: number
  active?: boolean
  onClick: () => void
  disabled?: boolean
  activeColor?: string
  label: string
}) => (
  <button onClick={onClick} disabled={disabled} className="flex flex-col items-center gap-1 group" aria-label={label}>
    <div className={`w-11 h-11 rounded-full flex items-center justify-center transition-all
      ${active ? 'bg-gray-100 scale-110' : 'bg-gray-100 group-hover:bg-gray-200'}
      ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
      <Icon className={`w-6 h-6 ${active ? activeColor : 'text-gray-700'}`} />
    </div>
    {count !== undefined && <span className="text-xs font-medium text-gray-600">{formatCount(count)}</span>}
  </button>
)

const ReelDetailPage = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user, isAuthenticated } = useAuthStore()
  const [reel, setReel] = useState<Reel | null>(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)

  const [isMuted, setIsMuted] = useState(true)
  const [liked, setLiked] = useState(false)
  const [likeCount, setLikeCount] = useState(0)
  const [bookmarked, setBookmarked] = useState(false)
  const [reshared, setReshared] = useState(false)
  const [reshareCount, setReshareCount] = useState(0)

  const [isLiking, setIsLiking] = useState(false)
  const [isBookmarking, setIsBookmarking] = useState(false)
  const [isResharing, setIsResharing] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const [showComments, setShowComments] = useState(false)
  const [comments, setComments] = useState<ReelComment[]>([])
  const [commentsLoading, setCommentsLoading] = useState(false)
  const [commentText, setCommentText] = useState('')
  const [reportReelId, setReportReelId] = useState<number | null>(null)

  const videoRef = useRef<HTMLVideoElement>(null)

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login')
    }
  }, [isAuthenticated, navigate])

  // Fetch the single reel (viewer-aware: likedByMe/isReshared reflect the
  // logged-in user) + its bookmarked status.
  useEffect(() => {
    let mounted = true
    const load = async () => {
      setLoading(true)
      setNotFound(false)
      try {
        const data = await reelService.getReel(Number(id))
        if (!mounted) return
        if (!data) { setNotFound(true); return }
        setReel(data)
        setLiked(!!data.likedByMe)
        setLikeCount(data.likeCount || 0)
        setReshared(!!data.isReshared)
        setReshareCount(data.reshareCount || 0)
        // Bookmark status is a separate per-reel check.
        try {
          const isBm = await reelService.isBookmarked(data.id)
          if (mounted) setBookmarked(!!isBm)
        } catch { /* anonymous/ignore */ }
      } catch (e) {
        console.error('Error fetching reel:', e)
        if (mounted) setNotFound(true)
      }
      if (mounted) setLoading(false)
    }
    if (id) load()
    return () => { mounted = false }
  }, [id])

  // Auto-play + increment view count once the reel loads.
  useEffect(() => {
    const video = videoRef.current
    if (video && reel) {
      video.src = getMediaUrl(reel.videoUrl)
      video.muted = isMuted
      video.play().catch(() => {})
    }
    return () => { if (video) video.pause() }
  }, [reel, isMuted])

  useEffect(() => {
    if (reel) {
      reelService.view(reel.id).catch(() => {})
    }
  }, [reel])

  const toggleMute = useCallback(() => {
    setIsMuted(prev => {
      const next = !prev
      if (videoRef.current) videoRef.current.muted = next
      return next
    })
  }, [])

  const togglePlay = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.paused ? videoRef.current.play() : videoRef.current.pause()
    }
  }, [])

  const handleLike = useCallback(async () => {
    if (!reel || isLiking) return
    setIsLiking(true)
    try {
      if (liked) {
        await reelService.unlike(reel.id)
        setLiked(false)
        setLikeCount(c => Math.max(0, c - 1))
      } else {
        await reelService.like(reel.id)
        setLiked(true)
        setLikeCount(c => c + 1)
      }
    } catch {
      alert('Failed to like. Please try again.')
    } finally {
      setIsLiking(false)
    }
  }, [reel, liked, isLiking])

  const handleBookmark = useCallback(async () => {
    if (!reel || isBookmarking) return
    setIsBookmarking(true)
    try {
      const result = await reelService.toggleBookmark(reel.id)
      setBookmarked(!!result)
    } catch (error: any) {
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        alert('Please log in to bookmark reels')
        navigate('/login')
      } else {
        alert('Failed to bookmark. Please try again.')
      }
    } finally {
      setIsBookmarking(false)
    }
  }, [reel, isBookmarking, navigate])

  // Share: the dropdown UI lives in <ShareMenu>. The server share-count bump
  // is best-effort — swallowed so a failure never blocks the copy/share or
  // shows an error.
  const handleSharedCount = useCallback(() => {
    if (!reel) return
    reelService.share(reel.id)
      .then(() => setReel(prev => prev ? { ...prev, shareCount: (prev.shareCount || 0) + 1 } : prev))
      .catch(() => {})
  }, [reel])

  const handleReshare = useCallback(async () => {
    if (!reel || isResharing) return
    setIsResharing(true)
    try {
      if (reshared) {
        await reelService.unreshare(reel.id)
        setReshared(false)
        setReshareCount(c => Math.max(0, c - 1))
      } else {
        await reelService.reshare(reel.id)
        setReshared(true)
        setReshareCount(c => c + 1)
      }
    } catch {
      alert('Failed to reshare. Please try again.')
    } finally {
      setIsResharing(false)
    }
  }, [reel, reshared, isResharing])

  const handleDelete = useCallback(async () => {
    if (!reel || isDeleting) return
    if (!window.confirm('Delete this reel permanently? This cannot be undone.')) return
    setIsDeleting(true)
    try {
      await reelService.delete(reel.id)
      navigate(-1)
    } catch {
      alert('Could not delete the reel. Please try again.')
    } finally {
      setIsDeleting(false)
    }
  }, [reel, isDeleting, navigate])

  const fetchComments = useCallback(async () => {
    if (!reel) return
    setCommentsLoading(true)
    try {
      const response = await reelService.getComments(reel.id)
      setComments(response.content || [])
    } catch {
      setComments([])
    } finally {
      setCommentsLoading(false)
    }
  }, [reel])

  const handleOpenComments = useCallback(() => {
    fetchComments()
    setShowComments(true)
  }, [fetchComments])

  const handleAddComment = useCallback(async () => {
    if (!reel || !commentText.trim()) return
    try {
      const newComment = await reelService.addComment(reel.id, commentText.trim())
      setComments(prev => [newComment, ...prev])
      setCommentText('')
      setReel(prev => prev ? { ...prev, commentCount: (prev.commentCount || 0) + 1 } : prev)
    } catch {
      alert('Failed to add comment. Please try again.')
    }
  }, [reel, commentText])

  if (loading) {
    return (
      <GovLayout showSidebar={false}>
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </GovLayout>
    )
  }

  if (notFound || !reel) {
    return (
      <GovLayout showSidebar={false}>
        <div className="max-w-md mx-auto py-12 text-center">
          <div className="text-4xl mb-3">🔍</div>
          <p className="font-medium">Reel not found</p>
          <p className="text-sm text-gray-500 mt-1">This reel may have been deleted.</p>
          <button onClick={() => navigate('/reels')} className="text-blue-600 text-sm mt-3">Back to reels</button>
        </div>
      </GovLayout>
    )
  }

  return (
    <GovLayout showSidebar={false}>
      <div className="max-w-md mx-auto">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 mb-3"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        {/* Video Player Card — mirrors ReelsPage */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="relative aspect-[9/16] bg-black rounded-xl overflow-hidden">
            <video
              ref={videoRef}
              poster={reel.thumbnailUrl ? getMediaUrl(reel.thumbnailUrl) : undefined}
              className="absolute inset-0 w-full h-full object-cover"
              loop
              playsInline
              muted={isMuted}
              onClick={togglePlay}
            />

            {/* Gradient Overlay */}
            <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-black/40 via-transparent to-black/60" />

            {/* Top Bar */}
            <div className="absolute top-0 left-0 right-0 p-3 flex items-center justify-between z-20">
              <button
                onClick={() => navigate(-1)}
                className="p-2 text-white bg-black/30 backdrop-blur-sm rounded-full hover:bg-black/50"
                aria-label="Back"
              >
                <ChevronDown className="w-5 h-5 rotate-45" />
              </button>
              <h2 className="text-white font-semibold text-base drop-shadow-lg">Reel</h2>
              <button
                onClick={toggleMute}
                className="p-2 text-white bg-black/50 backdrop-blur-sm rounded-full hover:bg-black/70"
                aria-label={isMuted ? 'Unmute' : 'Mute'}
              >
                {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
              </button>
            </div>

            {/* Mute indicator */}
            {isMuted && (
              <div onClick={toggleMute} className="absolute top-14 right-3 z-30 cursor-pointer">
                <div className="bg-red-500/80 text-white px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 hover:bg-red-600/80">
                  <VolumeX className="w-4 h-4" />
                  <span>Tap to Unmute</span>
                </div>
              </div>
            )}

            {/* Right Side Actions */}
            <div className="absolute right-3 bottom-20 flex flex-col items-center gap-4 z-30">
              <ActionButton
                icon={Heart}
                count={likeCount}
                active={liked}
                onClick={handleLike}
                disabled={isLiking}
                activeColor="text-red-500"
                label="Like"
              />
              <ActionButton
                icon={MessageCircle}
                count={reel.commentCount}
                onClick={handleOpenComments}
                label="Comments"
              />
              <ActionButton
                icon={Repeat}
                count={reshareCount}
                active={reshared}
                onClick={handleReshare}
                disabled={isResharing}
                activeColor="text-green-500"
                label="Reshare"
              />
              <div className="flex flex-col items-center gap-1">
                <ShareMenu
                  url={`${window.location.origin}/reel/${reel.id}`}
                  title="TB Awareness Reel"
                  text="Check out this reel on TB Mukt Bharat"
                  onShared={handleSharedCount}
                  triggerClassName="w-11 h-11 rounded-full flex items-center justify-center bg-gray-100 hover:bg-gray-200 text-gray-700 transition-all"
                />
                <span className="text-xs font-medium text-gray-600">{formatCount(reel.shareCount)}</span>
              </div>
              <ActionButton
                icon={Bookmark}
                active={bookmarked}
                onClick={handleBookmark}
                disabled={isBookmarking}
                activeColor="text-yellow-500"
                label="Save"
              />
              {user && reel.user.id === user.id && (
                <ActionButton
                  icon={Trash2}
                  onClick={handleDelete}
                  disabled={isDeleting}
                  activeColor="text-red-500"
                  label="Delete"
                />
              )}
              {user && reel.user.id !== user.id && (
                <ActionButton
                  icon={Flag}
                  onClick={() => setReportReelId(reel.id)}
                  activeColor="text-red-500"
                  label="Report"
                />
              )}
            </div>

            {/* Bottom Info */}
            <div className="absolute bottom-0 left-0 right-0 p-3 z-20">
              <div className="flex items-center gap-2.5 mb-2">
                <img
                  src={reel.user.profileImage ? getMediaUrl(reel.user.profileImage) : '/default-avatar.svg'}
                  alt={reel.user.firstName}
                  className="w-9 h-9 rounded-full object-cover border-2 border-white/30"
                  loading="lazy"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium text-sm drop-shadow-lg truncate">
                    {reel.user.firstName} {reel.user.lastName}
                  </p>
                  <p className="text-gray-300 text-xs">@{reel.user.username}</p>
                </div>
              </div>

              {reel.caption && (
                <p className="text-white text-sm mb-2 line-clamp-2 drop-shadow-lg">
                  <HashtagText
                    content={reel.caption}
                    onTagClick={(tag) => navigate(`/hashtag/${encodeURIComponent(tag.toLowerCase())}`)}
                    tagClassName="text-blue-300 hover:underline font-medium"
                  />
                </p>
              )}

              {reel.hashtags && reel.hashtags.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {reel.hashtags.map((tag) => (
                    <button
                      key={tag}
                      onClick={() => navigate(`/hashtag/${encodeURIComponent(tag.toLowerCase())}`)}
                      className="px-2 py-0.5 bg-white/15 text-white text-xs font-medium rounded-full hover:bg-white/25"
                    >
                      #{tag}
                    </button>
                  ))}
                </div>
              )}

              {reel.musicTitle && (
                <p className="text-white/80 text-xs drop-shadow-lg truncate">
                  ♪ {reel.musicTitle}{reel.musicArtist && ` - ${reel.musicArtist}`}
                </p>
              )}

              <p className="text-gray-400 text-xs mt-1.5">
                {formatDistanceToNow(new Date(reel.createdAt))} ago · {formatCount(reel.viewCount)} views
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Comments Drawer — mirrors ReelsPage */}
      {showComments && (
        <div className="fixed inset-0 bg-black/70 z-50" onClick={() => setShowComments(false)}>
          <div
            className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl max-h-[70vh] flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-bold">Comments ({reel.commentCount})</h3>
              <button onClick={() => setShowComments(false)} aria-label="Close comments">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {commentsLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : comments.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No comments yet. Be the first to comment!</p>
              ) : (
                <div className="space-y-4">
                  {comments.map(comment => (
                    <div key={comment.id} className="flex gap-3">
                      <img
                        src={comment.user.profileImage ? getMediaUrl(comment.user.profileImage) : '/default-avatar.svg'}
                        alt={comment.user.firstName}
                        className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                        loading="lazy"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm">
                          <span className="font-semibold text-gray-900">{comment.user.firstName} {comment.user.lastName}</span>
                          <span className="text-gray-600 ml-2">{comment.content}</span>
                        </p>
                        <p className="text-xs text-gray-500 mt-1">{formatDistanceToNow(new Date(comment.createdAt))} ago</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="p-4 border-t">
              <div className="flex gap-2">
                <img
                  src={user?.profileImage ? getMediaUrl(user.profileImage) : '/default-avatar.svg'}
                  alt={user?.firstName}
                  className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                  loading="lazy"
                />
                <input
                  type="text"
                  value={commentText}
                  onChange={e => setCommentText(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleAddComment()}
                  placeholder="Add a comment..."
                  className="flex-1 px-4 py-2 rounded-full bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <GovButton onClick={handleAddComment} disabled={!commentText.trim()}>
                  <Send className="w-4 h-4" />
                </GovButton>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Report reel modal — shown when a viewer flags a reel they don't own */}
      {reportReelId && (
        <ReportModal reelId={reportReelId} onClose={() => setReportReelId(null)} />
      )}
    </GovLayout>
  )
}

export default ReelDetailPage